import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import AccommodationDetails from './AccommodationDetails';
import Button from '../../shared/components/ui/Button';
import SaleOverview from './SaleOverview';
import { STAGE_HISTORY_AND_LOGS_TAB } from '../constants/leadDetailTabs';
import DealsDetails from './DealsDetails';
import ScheduleDetails from './ScheduleDetails';
import ActivityList from './ActivityList';
import DealsList from './DealsList';
import PostSalesDealHub from '../components/postSales/PostSalesDealHub';
import SaleAttachmentList from './SaleAttachmentList';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCloseDealsByLeadId, getLeadByID } from '../services/api/implementation/impl';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Logs from './Logs';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Loader from '../../shared/components/ui/Loader';
import WhatsAppMessageThread from '../components/whatsapp/WhatsAppMessageThread';
import useInboxStream from '../hooks/useInboxStream';
import { useSmartBack } from '../../shared/navigation';

const SaleDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const { collapsed } = useSidebar();
  const userId = Cookies.get('userId');
  const { onBack: handleBack } = useSmartBack({
    fallback: '/builder/sales/?view=leads&tab=Leads'
  });

  useEffect(() => {
    mixpanel.track('Sale Details Viewed', {
      user_id: userId,
      sale_id: id,
      timestamp: new Date().toISOString()
    });
  }, [userId, id]);

  const {
    data: leadbyId,
    isLoading,
    error,
    refetch: refetchLeadbyId
  } = useQuery({
    queryKey: ['leadById', parseInt(id)],
    queryFn: async () => {
      const res = await getLeadByID(parseInt(id));
      return res;
    },
    enabled: !!id, // Only run the query if id is truthy
    refetchOnWindowFocus: true,
    onSuccess: (data) => {
      mixpanel.track('Lead By ID API Success', {
        user_id: userId,
        lead_id: id,
        lead_name: data?.user?.name,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Lead By ID API Failed', {
        user_id: userId,
        lead_id: id,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  /* 
    Meta data
  */
  /* 
    Local state
  */
  const initialTab =
    location.state?.secondaryTab === STAGE_HISTORY_AND_LOGS_TAB
      ? STAGE_HISTORY_AND_LOGS_TAB
      : 'Overview';
  const [secondaryTab, setSecondaryTab] = useState(initialTab);

  /* 
    Data fetching
  */
  const { data: sale } = useState(null);

  const leadIdForPanels =
    leadbyId?.id != null
      ? parseInt(String(leadbyId.id), 10)
      : id
        ? parseInt(String(id), 10)
        : undefined;

  const isClosedWon = Boolean(
    leadbyId?.deal?.id ||
      leadbyId?.pstatus === 'Closed Won' ||
      leadbyId?.activities?.some((a) => a.name === 'Deal Closed')
  );

  const {
    data: dealsData,
    isLoading: isLoadingDeals,
    refetch: refetchDeals
  } = useQuery({
    queryKey: ['closedDeals', leadIdForPanels],
    queryFn: () => getCloseDealsByLeadId(leadIdForPanels),
    enabled: isClosedWon && !!leadIdForPanels,
    refetchOnWindowFocus: true
  });

  const currentDeal = useMemo(() => {
    if (!dealsData?.data || !leadIdForPanels) return null;
    const rows = Array.isArray(dealsData.data) ? dealsData.data : [dealsData.data];
    return rows.find((deal) => deal.lead_id === leadIdForPanels) ?? rows[0] ?? null;
  }, [dealsData?.data, leadIdForPanels]);

  // Normalize the lead's phone for the WhatsApp thread (matches the leads table action).
  const whatsappPhone = (() => {
    const raw = leadbyId?.user?.phone_number;
    if (!raw) return null;
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return null;
    return digits.length === 10 ? `91${digits}` : digits;
  })();

  const whatsappConversation = React.useMemo(
    () => ({
      phone_number: whatsappPhone,
      contact_name: leadbyId?.user?.name || ''
    }),
    [whatsappPhone, leadbyId?.user?.name]
  );

  // Keep the conversation live while the WhatsApp tab is open.
  useInboxStream({ enabled: secondaryTab === 'WhatsApp' && Boolean(whatsappPhone) });

  /* 
    JSX
  */
  return (
    <>
      <Loader isVisible={isLoading} />
      {/* Main content */}
      <Layout>
        {/* Header */}
        <div
          className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
    ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
  `}>
          <Header data={{ name: `Lead Details` }} backArrow={true} onBack={handleBack} />
        </div>

        {/* Secondary Tabbed content */}
        <div
          className={` ${collapsed ? 'w-[100%] pl-3' : 'w-[100%]'} flex flex-col justify-start items-center mt-[4rem]`}>
          <SaleOverview
            leadID={leadbyId?.id}
            type={isClosedWon ? 'deal' : undefined}
            combineHistoryAndLogsTabs
            activeHeaderTab={secondaryTab}
            onHeaderTabChange={(tab) => {
              mixpanel.track('Sale Details Tab Changed', {
                user_id: userId,
                sale_id: id,
                tab: tab,
                timestamp: new Date().toISOString()
              });
              setSecondaryTab(tab);
            }}
          />
        </div>

        <div
          className={` ${collapsed ? 'w-[100%] pl-7 pr-4' : 'w-[100%] pl-4 pr-4'} flex flex-col justify-start items-stretch`}>
          {secondaryTab === STAGE_HISTORY_AND_LOGS_TAB && (
            <div className="flex w-full min-w-0 flex-col gap-4 lg:flex-row lg:items-stretch">
              <div className="flex min-h-0 min-w-0 flex-1 basis-0 flex-col lg:w-1/2">
                {isClosedWon ? (
                  <DealsList
                    salesId={leadIdForPanels}
                    dealData={currentDeal}
                    isLoadingDeals={isLoadingDeals}
                    onDealUpdate={refetchDeals}
                    variant="split"
                  />
                ) : (
                  <ActivityList salesId={leadIdForPanels} variant="split" />
                )}
              </div>
              <div className="flex min-h-0 min-w-0 flex-1 basis-0 flex-col lg:w-1/2">
                <Logs salesId={leadIdForPanels} variant="split" />
              </div>
            </div>
          )}

          {secondaryTab === 'Post Sales' &&
            isClosedWon &&
            (currentDeal?.id ? (
              <PostSalesDealHub
                deal={currentDeal}
                lead={currentDeal?.lead ?? leadbyId}
                onDealUpdate={refetchDeals}
              />
            ) : (
              <div className="mt-2 flex min-h-[10rem] items-center justify-center rounded-2xl border border-slate-200/90 bg-white text-sm text-slate-500">
                {isLoadingDeals ? 'Loading deal…' : 'No closed deal found for this lead.'}
              </div>
            ))}

          {secondaryTab === 'WhatsApp' && whatsappPhone && (
            <div className="w-full">
              <div
                className="flex overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm"
                style={{ height: 'calc(100vh - 12rem)' }}>
                <WhatsAppMessageThread
                  key={whatsappPhone}
                  conversation={whatsappConversation}
                  leadId={leadIdForPanels}
                  projectId={leadbyId?.projects?.[0]?.id ?? null}
                />
              </div>
            </div>
          )}
        </div>
        {/* {secondaryTab === 'Deals' && (
          <div className="w-full h-full">
            <DealsDetails />
          </div>
        )} */}

        {/* Attachments content */}
        {/* {secondaryTab === 'Attachments' && (
          <div className=" w-full h-full ">
            <SaleAttachmentList />
          </div>
        )} */}

        {/* Schedule */}
        {/* {secondaryTab === 'Schedule' && (
          <div className=" w-full h-full ">
            <ScheduleDetails />
          </div>
        )} */}
      </Layout>
    </>
  );
};

export default SaleDetails;
